import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Form,
  Dropdown,
  Button,
  InputGroup
} from '@themesberg/react-bootstrap';
import { ChevronDown, X, Plus } from 'lucide-react';
import { apiCall } from '../../middlewares/api';
import { apiCallConsts } from '../../constants/apiCallConsts';
import { updateToastInfo } from '../../actions/settings';
import { useDispatch } from 'react-redux';
import { t } from 'i18next';
import ConfirmationModal from '../../components/common/confirmationModal';
import { updateSpinnerState } from '../../actions/spinner';

const CreateQuestion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Parse query params (id & clone)
  const searchParams = new URLSearchParams(location.search);
  const questionId = searchParams.get('id');
  const isCloning = searchParams.get('clone') === 'true';
  // If we have an id and we're not cloning, it's edit mode
  const isEdit = !!questionId && !isCloning;

  // Component State
  const [showConfirm, setShowConfirm] = useState(false);
  const [questionName, setQuestionName] = useState(''); // Question Name
  const [questionText, setQuestionText] = useState('');
  const [placeHolder, setPlaceHolder] = useState(''); // PlaceHolder (optional)
  const [questionType, setQuestionType] = useState('Input Text');
  const [answerOptions, setAnswerOptions] = useState(['']);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isRequired, setIsRequired] = useState(true); // isRequired toggle

  // Error state for required fields
  const [errors, setErrors] = useState({
    questionName: '',
    questionText: '',
    answerOptions: [] // Will be an array corresponding to each option
  });

  // Data arrays
  const questionTypes = [
    'Input Text',
    'Single Select (Radio Button)',
    'Multi Select (Check Box)',
    'Dropdown',
    'File Upload',
    'Number Input'
  ];
  const templates = [
    'Smartphone Exchange Qs',
    'Laptop Exchange Qs',
    'Tablet Exchange Qs'
  ];

  // Only show answer options if the question type requires it
  const showAnswerOptions = ['Single Select (Radio Button)', 'Multi Select (Check Box)', 'Dropdown'].includes(questionType);

  // Reverse mapping from API type values to friendly strings
  const typeMapping = {
    dropdown: 'Dropdown',
    radio: 'Single Select (Radio Button)',
    checkbox: 'Multi Select (Check Box)',
    file: 'File Upload',
    number: 'Number Input',
    text: 'Input Text'
  };

  // Fetch data from GET question by ID API if in edit/clone mode
  useEffect(() => {
    if (questionId) {
      apiCall(apiCallConsts.GET_METHOD, `internal/questions/${questionId}`)
        .then((response) => response)
        .then(({ data }) => {
          const existingQuestion = data;
          if (isCloning) {
            setQuestionName(`${existingQuestion.questionName} (Cloned)`);
            setQuestionText(`${existingQuestion.questionText} (Cloned)`);
          } else {
            setQuestionName(existingQuestion.questionName);
            setQuestionText(existingQuestion.questionText);
          }
          setQuestionType(
            typeMapping[existingQuestion.questionType?.toLowerCase()] ||
              existingQuestion.questionType?.toLowerCase()
          );
          setAnswerOptions(existingQuestion.answerOptions || ['']);
          setSelectedTemplate(existingQuestion.assignedTemplate);
          setPlaceHolder(existingQuestion.placeholder);
          setIsRequired(existingQuestion.isRequired || false);
        })
        .catch((e) => {
          dispatch(
            updateToastInfo({
              show: true,
              type: 'danger',
              title: 'Failed!',
              message: t(e.message)
            })
          );
        });
    }
  }, [questionId, isCloning, dispatch]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!questionName.trim()) {
      newErrors.questionName = 'Question Name is required';
      isValid = false;
    } else {
      newErrors.questionName = '';
    }

    if (!questionText.trim()) {
      newErrors.questionText = 'Question Text is required';
      isValid = false;
    } else {
      newErrors.questionText = '';
    }

    if (showAnswerOptions) {
      const answerErrors = answerOptions.map((option) =>
        option.trim() ? '' : 'Answer Option is required'
      );
      // If any errors found in answer options, mark as invalid
      if (answerErrors.some((err) => err !== '')) {
        isValid = false;
      }
      newErrors.answerOptions = answerErrors;
    } else {
      newErrors.answerOptions = [];
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handlers
  const handleCancel = () => navigate('/');

  const saveChanges = async () => {
    // Hide the modal regardless (will be re-opened if needed)
    setShowConfirm(false);

    const payload = {
      name: questionName,           // Question Name from form
      placeholder: placeHolder,     // Optional placeholder
      options: showAnswerOptions ? answerOptions : [],
      label: questionText,
      questionId: questionId || `q_${Date.now()}`,
      type:
        questionType === 'Dropdown'
          ? 'dropdown'
          : questionType === 'Single Select (Radio Button)'
          ? 'radio'
          : questionType === 'Multi Select (Check Box)'
          ? 'checkbox'
          : questionType === 'File Upload'
          ? 'file'
          : questionType === 'Number Input'
          ? 'number'
          : 'text',
      format: questionType === 'Multi Select (Check Box)' ? 'multi' : 'single',
      isActive: true,
      isRequired // pass the isRequired flag
    };

    try {
      dispatch(updateSpinnerState(true));
      let response;
      if (isEdit) {
        // Update API call
        response = await apiCall(
          apiCallConsts.PUT_METHOD,
          `internal/questions/${questionId}`,
          payload
        );
        navigate(-1);
      } else {
        // Create/Clone API call
        response = await apiCall(apiCallConsts.POST_METHOD, 'internal/questions', payload);
        // Reset form fields
        setQuestionName('');
        setPlaceHolder('');
        setAnswerOptions(['']);
        setQuestionType('Input Text');
        setQuestionText('');
      }
      dispatch(updateSpinnerState(false));

      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: 'Success',
          message: isEdit
            ? `Question with ID ${questionId} updated successfully!`
            : isCloning
            ? 'Cloned question created successfully!'
            : 'New question created successfully!'
        })
      );
    } catch (e) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: t(e.message)
        })
      );
    }
  };

  // When user clicks Save / Create, first validate then show confirmation if valid.
  const handleSave = () => {
    if (validateForm()) {
      setShowConfirm(true);
    }
  };

  const addAnswerOption = () => {
    setAnswerOptions([...answerOptions, '']);
    // Extend errors array as well
    setErrors((prevErrors) => ({
      ...prevErrors,
      answerOptions: [...(prevErrors.answerOptions || []), '']
    }));
  };

  const updateAnswerOption = (index, value) => {
    const newOptions = [...answerOptions];
    newOptions[index] = value;
    setAnswerOptions(newOptions);

    // Remove error if valid text is entered
    setErrors((prevErrors) => {
      const newAnswerErrors = [...(prevErrors.answerOptions || [])];
      newAnswerErrors[index] = value.trim() ? '' : 'Answer Option is required';
      return { ...prevErrors, answerOptions: newAnswerErrors };
    });
  };

  const removeAnswerOption = (index) => {
    setAnswerOptions(answerOptions.filter((_, i) => i !== index));
    setErrors((prevErrors) => ({
      ...prevErrors,
      answerOptions: prevErrors.answerOptions.filter((_, i) => i !== index)
    }));
  };

  return (
    <Container
      fluid
      style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '40px 20px' }}
    >
      {/* Heading Section */}
      <Row style={{ marginBottom: '24px' }}>
        <h3 style={{ fontWeight: 500, margin: 0 }}>
          Exchange Questions &gt; {isCloning ? 'Clone Question' : isEdit ? 'Edit Question' : 'Create New Question'}
        </h3>
      </Row>

      <Row style={{ marginBottom: '24px', padding: '16px', borderRadius: '4px', backgroundColor: '#ffffff' }}>
        <h6 style={{ fontWeight: 500, margin: 0 }}>
          {isCloning
            ? 'Clone an Existing Question'
            : isEdit
            ? 'Update Existing Question'
            : 'Add New Question'}
        </h6>
      </Row>

      {/* Question Form Section */}
      <Row style={{ marginBottom: '24px', padding: '16px', borderRadius: '4px', backgroundColor: '#ffffff' }}>
        {/* Left Column - Label */}
        <div
          style={{
            width: '20%',
            padding: '8px',
            display: 'flex',
            alignItems: 'start',
            fontWeight: 500
          }}
        >
          Question Details
        </div>
        {/* Right Column - Form */}
        <div style={{ width: '80%', padding: '16px' }}>
          {/* Question Name */}
          <Form.Group className="mb-3" controlId="questionName">
            <Form.Label style={{ marginBottom: '8px', fontWeight: 500 }}>Question Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter a short title for the question"
              value={questionName}
              onChange={(e) => {
                setQuestionName(e.target.value);
                // Clear error when text is entered
                if (e.target.value.trim()) {
                  setErrors((prev) => ({ ...prev, questionName: '' }));
                }
              }}
              style={{
                backgroundColor: '#f8f9fa',
                border: errors.questionName ? '1px solid red' : '1px solid #dee2e6',
                borderRadius: 0,
                height: '40px'
              }}
            />
            {errors.questionName && (
              <Form.Text style={{ color: 'red' }}>{errors.questionName}</Form.Text>
            )}
          </Form.Group>

          {/* Question Text */}
          <Form.Group className="mb-3" controlId="questionText">
            <Form.Label style={{ marginBottom: '8px', fontWeight: 500 }}>Question Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="What is the condition of the screen?"
              value={questionText}
              onChange={(e) => {
                setQuestionText(e.target.value);
                if (e.target.value.trim()) {
                  setErrors((prev) => ({ ...prev, questionText: '' }));
                }
              }}
              style={{
                backgroundColor: '#f8f9fa',
                border: errors.questionText ? '1px solid red' : '1px solid #dee2e6',
                borderRadius: 0,
                height: '50px'
              }}
            />
            {errors.questionText && (
              <Form.Text style={{ color: 'red' }}>{errors.questionText}</Form.Text>
            )}
          </Form.Group>

          {/* PlaceHolder Field (optional) */}
          <Form.Group className="mb-3" controlId="placeHolder">
            <Form.Label style={{ marginBottom: '8px', fontWeight: 500 }}>PlaceHolder</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter placeholder text (if any)"
              value={placeHolder}
              onChange={(e) => setPlaceHolder(e.target.value)}
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: 0,
                height: '40px'
              }}
            />
          </Form.Group>

          {/* Question Type */}
          <Form.Group className="mb-3" controlId="questionType">
            <Form.Label style={{ marginBottom: '8px', fontWeight: 500 }}>Question Type</Form.Label>
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                style={{
                  width: '100%',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: 0,
                  height: '40px'
                }}
              >
                {questionType}
                <ChevronDown size={16} />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '100%', borderRadius: 0 }}>
                {questionTypes.map((type, index) => (
                  <Dropdown.Item key={index} onClick={() => setQuestionType(type)}>
                    {type}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {/* Answer Options / Placeholder */}
          {showAnswerOptions ? (
            <Form.Group className="mb-3" controlId="answerOptions">
              <Form.Label style={{ marginBottom: '8px', fontWeight: 500 }}>Answer Options</Form.Label>
              <br />
              {answerOptions.map((option, index) => (
                <InputGroup key={index} className="mb-2">
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: 0,
                      height: '40px'
                    }}
                  >
                    {questionType.includes('Multi') ? (
                      <Form.Check type="checkbox" disabled />
                    ) : (
                      <Form.Check type="radio" disabled />
                    )}
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Enter option text"
                    value={option}
                    onChange={(e) => updateAnswerOption(index, e.target.value)}
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: errors.answerOptions[index]
                        ? '1px solid red'
                        : '1px solid #dee2e6',
                      borderRadius: 0,
                      height: '40px'
                    }}
                  />
                  <Button
                    variant="light"
                    onClick={() => removeAnswerOption(index)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: 0,
                      height: '40px'
                    }}
                  >
                    <X size={18} />
                  </Button>
                </InputGroup>
              ))}
              {errors.answerOptions &&
                errors.answerOptions.map(
                  (errMsg, index) =>
                    errMsg && (
                      <Form.Text key={index} style={{ color: 'red' }}>
                        {errMsg}
                      </Form.Text>
                    )
                )}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  height: '40px'
                }}
              >
                <div></div>
                <Button
                  variant="light"
                  onClick={addAnswerOption}
                  style={{
                    border: '1px solid #dee2e6',
                    marginTop: '8px',
                    borderRadius: 0
                  }}
                >
                  <Plus size={16} className="me-1" /> Add More Options
                </Button>
              </div>
            </Form.Group>
          ) : (
            <Form.Group className="mb-3" controlId="answerPlaceholder">
              <Form.Label style={{ marginBottom: '8px', fontWeight: 500 }}>Answer Options</Form.Label>
              <Form.Control
                type="text"
                readOnly
                style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 0,
                  height: '40px'
                }}
                value={
                  questionType === 'Input Text'
                    ? '(User-entered text)'
                    : questionType === 'File Upload'
                    ? '(Image Upload Button)'
                    : questionType === 'Number Input'
                    ? '(User enters a number)'
                    : '(User-entered text)'
                }
              />
            </Form.Group>
          )}

          {/* Is Required Toggle */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <Form.Check 
              type="checkbox"
              label="Is Required"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              style={{ fontWeight: 500 }}
            />
          </div>
        </div>
      </Row>

      {/* Template Section */}
      <Row style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#ffffff' }}>
        <div
          style={{
            width: '20%',
            padding: '8px',
            display: 'flex',
            alignItems: 'start',
            fontWeight: 500
          }}
        >
          Assign Template
        </div>
        <div style={{ width: '80%', padding: '16px' }}>
          <Form.Group className="mb-3" controlId="templateSelect">
            <Form.Label style={{ marginBottom: '8px', fontWeight: 500 }}>
              Select Existing Template
            </Form.Label>
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                style={{
                  width: '100%',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: 0,
                  height: '40px'
                }}
              >
                {selectedTemplate || 'Select template'}
                <ChevronDown size={16} />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '100%', borderRadius: 0 }}>
                {templates.map((template, index) => (
                  <Dropdown.Item key={index} onClick={() => setSelectedTemplate(template)}>
                    {template}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
          <Button
            variant="light"
            style={{
              border: '1px solid #dee2e6',
              borderRadius: 0,
              height: '40px'
            }}
            onClick={() => {
              navigate('/templete/create');
            }}
          >
            <Plus size={16} className="me-1" /> Create New Template
          </Button>
        </div>
      </Row>

      {/* Bottom Buttons */}
      <Row>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button
            variant="light"
            onClick={handleCancel}
            style={{ border: '1px solid #dee2e6', borderRadius: 0, height: '40px' }}
          >
            Cancel
          </Button>
          <Button
            variant="light"
            onClick={handleSave}
            style={{ borderRadius: 0, height: '40px' }}
          >
            {isCloning ? 'Clone' : isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </Row>

      {/* Confirmation Modal */}
      <ConfirmationModal
        showModal={showConfirm}
        onConfirm={saveChanges}
        closeModal={() => setShowConfirm(false)}
        modalBody={
          isCloning
            ? 'Are you sure you want to clone this question?'
            : isEdit
            ? 'Are you sure you want to update this question?'
            : 'Are you sure you want to create the question?'
        }
        modalTitle={t(
          isCloning ? 'Clone Question' : isEdit ? 'Update Question' : 'Create Question'
        )}
        confirmationModalProps={[]}
        setConfirmationModalProps={() => {}}
      />
    </Container>
  );
};

export default CreateQuestion;
